import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Sparkles, Lock, AlertTriangle } from 'lucide-react';

/**
 * TrialBanner - Shows trial status at top of dashboard
 * Displays different states: active trial, expired (read-only), or no trial
 */
export const TrialBanner = ({ trialStatus, daysRemaining, isReadOnly }) => {
  // Don't show banner for purchased users or users without trial
  if (!trialStatus || trialStatus === 'converted' || trialStatus === 'none') {
    return null;
  }

  // Active trial
  if (trialStatus === 'active' && daysRemaining !== undefined) {
    const urgency = daysRemaining <= 2 ? 'urgent' : daysRemaining <= 4 ? 'warning' : 'normal';
    
    return (
      <div 
        className={`px-4 py-3 flex items-center justify-between gap-4 flex-wrap ${
          urgency === 'urgent' 
            ? 'bg-red-500/10 border-b border-red-500/20' 
            : urgency === 'warning'
            ? 'bg-amber-500/10 border-b border-amber-500/20'
            : 'bg-blue-500/10 border-b border-blue-500/20'
        }`}
        data-testid="trial-banner-active"
      >
        <div className="flex items-center gap-3">
          <div className={`p-1.5 rounded-lg ${
            urgency === 'urgent' 
              ? 'bg-red-500/20' 
              : urgency === 'warning'
              ? 'bg-amber-500/20'
              : 'bg-blue-500/20'
          }`}>
            <Clock className={`w-4 h-4 ${
              urgency === 'urgent' 
                ? 'text-red-400' 
                : urgency === 'warning'
                ? 'text-amber-400'
                : 'text-blue-400'
            }`} />
          </div>
          <div>
            <p className={`text-sm font-medium ${
              urgency === 'urgent' 
                ? 'text-red-300' 
                : urgency === 'warning'
                ? 'text-amber-300'
                : 'text-blue-300'
            }`}>
              Trial Active: {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
            </p>
            <p className="text-xs text-zinc-400">
              Your notes will remain accessible even after trial ends
            </p>
          </div>
        </div>
        <Link
          to="/it-leadership#pricing"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            urgency === 'urgent' 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : urgency === 'warning'
              ? 'bg-amber-600 hover:bg-amber-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
          data-testid="trial-upgrade-button"
        >
          Upgrade Now
        </Link>
      </div>
    );
  }

  // Expired trial - read-only mode
  if (trialStatus === 'expired' && isReadOnly) {
    return (
      <div 
        className="bg-zinc-800 border-b border-zinc-700 px-4 py-3 flex items-center justify-between gap-4 flex-wrap"
        data-testid="trial-banner-expired"
      >
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-zinc-700">
            <Lock className="w-4 h-4 text-zinc-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-zinc-300">
              Trial Ended — Read-Only Mode
            </p>
            <p className="text-xs text-zinc-500">
              You can view your notes but cannot create new ones. Upgrade to continue.
            </p>
          </div>
        </div>
        <Link
          to="/it-leadership#pricing"
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
          data-testid="trial-upgrade-button"
        >
          Continue with Full Access
        </Link>
      </div>
    );
  }

  // Deleted state
  if (trialStatus === 'deleted') {
    return (
      <div 
        className="bg-zinc-800 border-b border-zinc-700 px-4 py-3 flex items-center justify-between gap-4 flex-wrap"
        data-testid="trial-banner-deleted"
      >
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-zinc-700">
            <AlertTriangle className="w-4 h-4 text-zinc-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-zinc-300">
              Trial Data Expired
            </p>
            <p className="text-xs text-zinc-500">
              Your trial notes have been removed. Start fresh with a full subscription.
            </p>
          </div>
        </div>
        <Link
          to="/it-leadership#pricing"
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          Get Started
        </Link>
      </div>
    );
  }

  return null;
};

/**
 * TrialStatusBadge - Small badge to show trial status in headers
 */
export const TrialStatusBadge = ({ trialStatus, daysRemaining }) => {
  if (!trialStatus || trialStatus === 'converted' || trialStatus === 'none') {
    return null;
  }

  if (trialStatus === 'active') {
    const urgency = daysRemaining <= 2 ? 'urgent' : daysRemaining <= 4 ? 'warning' : 'normal';
    
    return (
      <span 
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
          urgency === 'urgent' 
            ? 'bg-red-500/20 text-red-400' 
            : urgency === 'warning'
            ? 'bg-amber-500/20 text-amber-400'
            : 'bg-blue-500/20 text-blue-400'
        }`}
        data-testid="trial-status-badge"
      >
        <Sparkles className="w-3 h-3" />
        Trial: {daysRemaining}d left
      </span>
    );
  }

  if (trialStatus === 'expired') {
    return (
      <span 
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-zinc-700 text-zinc-400"
        data-testid="trial-status-badge"
      >
        <Lock className="w-3 h-3" />
        Read-only
      </span>
    );
  }

  return null;
};

/**
 * ReadOnlyNotice - Inline notice for when user tries to create content in read-only mode
 */
export const ReadOnlyNotice = ({ className = '' }) => {
  return (
    <div 
      className={`bg-zinc-800 border border-zinc-700 rounded-lg p-4 text-center ${className}`}
      data-testid="read-only-notice"
    >
      <Lock className="w-6 h-6 text-zinc-500 mx-auto mb-2" />
      <p className="text-zinc-300 text-sm font-medium mb-1">Read-Only Mode</p>
      <p className="text-zinc-500 text-xs mb-3">
        Your trial has ended. Upgrade to create new reflections.
      </p>
      <Link
        to="/it-leadership#pricing"
        className="inline-block px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
      >
        Upgrade Now
      </Link>
    </div>
  );
};

export default TrialBanner;
